import { Helmet } from 'react-helmet-async';
import { DEFAULT_SEO, SITE_ORIGIN, type SeoInput, type Translated } from './seoConfig';
import { useLanguage } from '../context/LanguageContext';

function absoluteUrl(pathOrUrl: string) {
  if (!pathOrUrl) return '';
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) return pathOrUrl;
  return `${SITE_ORIGIN}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

function resolveTranslated(value: string | Translated<string>, language: 'fr' | 'en') {
  if (typeof value === 'string') return value;
  return language === 'en' ? value.en : value.fr;
}

function toAlternatePath(currentPath: string, targetLanguage: 'fr' | 'en') {
  const isEn = currentPath === '/en' || currentPath.startsWith('/en/');
  if (targetLanguage === 'en') return isEn ? currentPath : currentPath === '/' ? '/en' : `/en${currentPath}`;
  return isEn ? (currentPath === '/en' ? '/' : currentPath.replace(/^\/en/, '')) : currentPath;
}

export function Seo(props: SeoInput) {
  const { language } = useLanguage();

  const title = resolveTranslated(props.title ?? DEFAULT_SEO.title, language);
  const description = resolveTranslated(props.description ?? DEFAULT_SEO.description, language);
  const canonicalPath = resolveTranslated(props.canonicalPath ?? DEFAULT_SEO.canonicalPath, language);
  const canonicalUrl = absoluteUrl(canonicalPath);
  const ogImageUrl = absoluteUrl(props.ogImagePath ?? DEFAULT_SEO.ogImagePath);
  const noindex = props.noindex ?? false;

  const alternateFr = absoluteUrl(toAlternatePath(canonicalPath, 'fr'));
  const alternateEn = absoluteUrl(toAlternatePath(canonicalPath, 'en'));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Burok',
    url: SITE_ORIGIN,
    logo: absoluteUrl('/assets/BUROKORANGELOGOSMALLER.png'),
  };

  return (
    <Helmet>
      <html lang={language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="fr" href={alternateFr} />
      <link rel="alternate" hrefLang="en" href={alternateEn} />
      <link rel="alternate" hrefLang="x-default" href={alternateFr} />

      {noindex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow" />}

      <meta property="og:site_name" content="Burok" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImageUrl} />

      <meta name="twitter:card" content={DEFAULT_SEO.twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}

